import Vue from 'vue';
import Vuex from 'vuex';
import {v4 as uuidv4} from 'uuid'
import {
  GenerateEffect,
  GetCryptoPaymentStatus,
  GetEffectGenerationSession,
  GetSubscription, GetWallPosts,
  PaySubscription,
  GetPublicSponsorName,
  SetPublicSponsorName,
  CreateGiftSubscriptionPurchase,
  GetGiftSubscriptionPurchase,
  GetGiftSubscriptionRedeemPreview,
  RedeemGiftSubscription,
  PostGeneratedEffect,
  Subscribe,
  CheckoutSubscribe,
  Unsubscribe,
  SubscribeCrypto, CancelPendingSubscription,
} from "@/api/api";
import {SponsorshipLevel} from "./models/SponsorshipLevel.js";
import {Subscription} from "./models/Subscription.js";
import SubscriptionStatus from "./models/SubscriptionStatus.js";
import {EffectGenerationStylePreset} from "./models/EffectGenerationStylePreset.js";
import EffectGenerationSession from "./models/EffectGenerationSession.js";
import WallPost from "./models/WallPost.js";
import {i18n} from '@/main';
import router from "../router";
import {logWarn, logError} from '@/helpers';
import SubscriptionPaymentMethodStatus from "./models/SubscriptionPaymentMethodStatus";
import EffectGenerationAttempt, {EffectGenerationAttemptStatus} from "@/store/models/EffectGenerationAttempt";

Vue.use(Vuex);

function getUUID() {
    return uuidv4().toString().replace(/-/g, '');
}

function currentAttempt(state) {
  return state.effectGenerationSession?.attempts?.[state.selectedAttemptIndex];
}

function isNewDesignActive(ctx) {
  try {
    return !!(ctx.getters?.newDesignEnabled) || !!window.appConfig?.new_design_enabled;
  } catch (e) {
    logWarn('Failed to read newDesignEnabled from Vuex store, falling back to appConfig flag', e);
    return !!window.appConfig?.new_design_enabled;
  }
}

function resolveDesignPath(ctx, legacyPath, wallPath) {
  return isNewDesignActive(ctx) ? wallPath : legacyPath;
}

function normalizePendingInfo(raw) {
  if (!raw) return null;
  const info = raw || {};
  return {
    nextLevelName: info.nextLevelName || info.next_level_name || '',
    startedAt: info.startedAt || info.started_at || null,
    expiresAt: info.expiresAt || info.expires_at || null,
  };
}

export default new Vuex.Store({
    state: {
        // Available effects.
        effects: [],

        // Frontend mode selected by channel configuration: stream or checkout.
        frontendMode: 'stream',
        checkoutMinAmount: 0,

        // Whether the one-time donations are available
        oneTimeDonationsAvailable: false,

        // ID of the currently selected effect.
        selectedEffect: null,

        // Effect preview.
        previewedEffect: null,

        // Whether the stream is currently live.
        stream: false,

        // Live flag (new, derived from backend is_live)
        isLive: false,

        // Current screen mode.
        mode: '',

        // Donation amount in the form.
        total: 0,

        // URL pointing to initiation of channel membership.
        joinUrl: '',

        // Whether the user has logged in with YouTube.
        authenticatedWithYoutube: false,

        // Whether the user has logged in as subscriber with Google or iCloud.
        authenticatedAsSubscriber: false,

        // Whether currently authenticated user is a sponsor of the channel.
        sponsor: false,

        // Whether currently authenticated user have active subscription.
        subscriber: false,

        // Whether subscriptions are enabled on channel.
        subscriptionsEnabled: false,

        // Whether free effects for youtube memebers and subscribers are available.
        youtubeMembershipFreeEffectsAvailable: false,

        // Array of sponsorship levels (Sorted by price_per_year_in_usd). Each level is an SponsorshipLevel object.
        sponsorshipLevels: [],

        // Identifer of selected level. Null if no level is selected. User can't select level if it's already active (current/paid/unpaid).
        selectedLevelID: null,

        // Object containing current paid subscription data (keys: 'id', 'external_id', 'paid_till', 'next_recurrent_payment', 'last_payment', 'recurrent', 'payment_failed', 'next_level', 'in_payment').
        currentSubscription: null,

        // Descriptors of effect classes.
        effectClasses: [],

        // Allowed cryptocurrencies (dict with allowed cryptocurrencies with it params:  {"BTC":{"precision":8,"uri_template":"bitcoin:${address}?amount=${amount}"}, ...)
        allowedCryptocurrencies: {},

        // Allowed payment methods (array with allowed payment methods: ['donation', 'BTC' ...])
        allowedPaymentMethods: [],

        // Third party payment methods (array with records like {"id": 1, "text": "foo", "url": "https://example.com"})
        thirdPartyPaymentMethods: [],

        // Remaining free limits of the effect classes for Subscribers and Youtube members.
        limits: {},

        // Index of free effect grants by subscription level ID. Example: { 1:[{ "level_id":1, "class_id":2, "class_name":"foo", limit:3 }, ... ], ... }
        subscriptionFreeEffectsIndex: {},

        // Whether subscription mode selector is shown (for DD subscribers and youtube).
        subscriptionModeSelectorVisible: false,

        // Currently selected payment mode in the form ('donation', 'subscription', 'BTC' ...).
        paymentMode: 'donation',

        // Channel visual settings.
        visuals: {},

        // Channel rate_multiplierss.
        rateMultipliers: {},

        // Whether effect is being posted.
        effectPostInProgress: false,

        // Whether unsubscribe/subscribe or pay request is in progress.
        subscriptionRequestInProgress: false,

        subscriptionUpdateTimerId: null,

        // Error message in user's language.
        errorMessage: null,
        errorTimer: null,

        // Cryptocurrency payment data
        paymentAddress: null,
        paymentAddressUUID: null,
        paymentQRCode: null,
        amount: null,
        cryptocurrencyPaymentMode: null,
        cryptocurrencyPaymentPurpose: null, // 'donation' | 'subscription' | 'subscription_gift'
        subscriptionPaymentIntent: null, // { levelId, months, paymentCurrency }
        giftPurchaseIntent: null, // { purchaseId, levelId, months, quantity, paymentCurrency }
        currentGiftPurchase: null, // { id, status, links[] ... }
        currentGiftPurchases: [], // [{ id, status, links[] ... }]
        // Cached upgrade credit for current subscription (from backend), in channel currency.
        subscriptionCredit: 0,

        // Effect generation
        effectGenerationSession: null,
        effectGenerationSessionChecked: false,
        availableEffectGenerationStylePresets: [],
        selectedEffectGenerationStylePresetID: null,
        maxGenerationAttempts: 3,
        selectedAttemptIndex: 0,
        selectedAttemptStatus: null,
        generationInProgress: false,
        generationDisabledGlobally: false,
        generationStatusPollTimerId: null,

        // Free effects for all sponsors
        freeEffectsEnabled: false,
        freeGenerationEnabled: false,
        freeGenerationSessionsLeft: 0,
        freeEffectsPaymentOptionName: null,
        secondsUntilNextFreeEffect: 0,
        secondsUntilNextFreeEffectIntervalID: null,
        googleRecaptchaSiteKey: null,

        // Sponsors wall
        wallEnabled: false,
        wallEnabledGlobal: true,
        wallEnabledChannel: true,
        // New design flag
        newDesignEnabled: false,
        posts: [],
        postsIndex: {},
        postsPagination: { nextPageToken: null, loading: false },

        // Public subscribers stats (e.g., active subscribers count)
        subscribersStats: null,

        // Public display name of the sponsor (optional)
        publicSponsorName: '',
        publicNameSaveInProgress: false,
        publicNameLoaded: false,

        // Subscription periods (months) and discounts
        subscriptionPeriods: [1],
        subscriptionDiscountsEnabled: false,

        // Generation attempts per mode (from AppSettings)
        generationAttempts: { free: 3, subscription: 3, donation: 5 },
        pendingUpgradeInfo: null,
        pendingCryptoPayment: null,
    },
    getters: {
        // New design flag
        newDesignEnabled(state) {
            return !!state.newDesignEnabled;
        },
        frontendMode(state) {
            return state.frontendMode || 'stream';
        },
        checkoutFrontendEnabled(state) {
            return state.frontendMode === 'checkout';
        },
        checkoutMinAmount(state) {
            return state.checkoutMinAmount || state.visuals?.minDonation || 1;
        },
        // Wall feature flag
        wallEnabled(state) {
            return state.wallEnabled;
        },
        wallEnabledGlobal(state) { return state.wallEnabledGlobal; },
        wallEnabledChannel(state) { return state.wallEnabledChannel; },
        // Combined check for subscriber or YouTube sponsor
        isSponsor(state) {
            return !!(state.authenticatedAsSubscriber && state.subscriber && state.subscriptionsEnabled);
        },
        // Posts (already adapted by backend)
        posts(state) {
            return state.posts || [];
        },
        cryptocurrencyPaymentMode(state) {
            return state.cryptocurrencyPaymentMode;
        },
        rateMultipliers(state) {
            return state.rateMultipliers;
        },
        effects(state) {
            return state.effects;
        },
        oneTimeDonationsAvailable(state) {
          return state.oneTimeDonationsAvailable;
        },
        effectIndex(state) {
            let index = {};
            for (let effect of state.effects) {
                index[effect.id] = effect;
            }
            return index;
        },
        stream(state) {
            return state.stream;
        },
        mode(state) {
            return state.mode;
        },
        donationOnlyMode(state) {
            return state.visuals.effectsSelectionMode == "disabled";
        },
        subscriptionModeSelectorVisible(state) {
            return state.subscriptionModeSelectorVisible;
        },
        subscriptionFreeEffectsIndex(state) {
            return state.subscriptionFreeEffectsIndex;
        },
        freeEffectsAvailable(state) {
            for (const [key, value] of Object.entries(state.limits)) {
                if (value > 0) {
                    return true;
                }
            }
            return false;
        },
        paymentModeAvailable(state) {
            if (state.allowedPaymentMethods.includes(state.paymentMode) && state.oneTimeDonationsAvailable) {
                return true;
            }
            if (state.paymentMode == 'subscription' && ((state.authenticatedAsSubscriber && state.subscriber && state.subscriptionsEnabled) || (state.authenticatedWithYoutube && state.sponsor && state.visuals.youtubeMembership))) {
                for (const [key, value] of Object.entries(state.limits)) {
                    if (value > 0) {
                        return true;
                    }
                }
            }
            if (state.paymentMode == 'free' && state.secondsUntilNextFreeEffect <= 0) {
              return true;
            }
            return false;
        },
        effectSelectorEnabled(state) {
            return state.visuals.effectsSelectionMode == "manual";
        },
        paidMode(state) {
            return state.allowedPaymentMethods.includes(state.paymentMode);
        },
        total(state) {
            return state.total;
        },
        selectedEffect(state) {
            return state.selectedEffect;
        },
        selectedEffectIsPreset(state) {
          return state.effects.find(effect => effect.id === state.selectedEffect)?.effectType === 'preset';
        },
        previewedEffect(state) {
            return state.previewedEffect;
        },
        limits(state) {
            return state.limits;
        },
        effectClasses(state) {
            return state.effectClasses;
        },
        sponsor(state) {
            return state.sponsor;
        },
        subscriber(state) {
            return state.subscriber;
        },
        joinUrl(state) {
            return state.joinUrl;
        },
        authenticated(state) {
            return state.authenticatedWithYoutube || state.authenticatedAsSubscriber;
        },
        authenticatedWithYoutube(state) {
            return state.authenticatedWithYoutube;
        },
        authenticatedAsSubscriber(state) {
            return state.authenticatedAsSubscriber;
        },
        subscriptionsEnabled(state) {
            return state.subscriptionsEnabled;
        },
        youtubeMembershipFreeEffectsAvailable(state) {
            return state.youtubeMembershipFreeEffectsAvailable;
        },
        sponsorshipLevels(state) {
            let levels = [];
            state.sponsorshipLevels.forEach((level) => {
                let levelCopy = level.clone();
                levelCopy.grants = state.subscriptionFreeEffectsIndex[level.externalID] || [];
                levels.push(levelCopy);
            });
            return levels.sort((left, right) => {
                const yearlyPriceDiff = Number(left.pricePerYearInUSD || 0) - Number(right.pricePerYearInUSD || 0);
                if (Math.abs(yearlyPriceDiff) > 0.000001) {
                    return yearlyPriceDiff;
                }
                const nameCompare = (left.name || '').localeCompare(right.name || '');
                if (nameCompare !== 0) {
                    return nameCompare;
                }
                return Number(left.ID || 0) - Number(right.ID || 0);
            });
        },
        currentLevel(state, getters) {
          const sub = getters.currentSubscription;
          let level = sub?.level;
          if (!level) {
            return null;
          }
          level = level.clone();
          level.grants = state.subscriptionFreeEffectsIndex[level.externalID] || [];
          return level;
        },
        nextLevel(state, getters) {
          const sub = getters.currentSubscription;
          let level = sub?.nextLevel;
          if (!level) return null;
          // Treat upgrade (more expensive next level) as not scheduled until payment is completed,
          // to match legacy behavior when user leaves Stripe without paying.
          if (sub && level.pricePerYearInUSD > sub.level.pricePerYearInUSD) {
            return null;
          }
          level = level.clone();
          level.grants = state.subscriptionFreeEffectsIndex[level.externalID] || [];
          return level;
        },
        selectedLevelID(state) {
            return state.selectedLevelID;
        },
        currentSubscription(state) {
            const subscription = state.currentSubscription;
            if (!subscription) {
              return null;
            }
            if (subscription.paymentMethodStatus === SubscriptionPaymentMethodStatus.Initiated) {
              return null;
            }
            if (subscription.status !== SubscriptionStatus.Paid) {
              return null;
            }
            return subscription;
        },
        subscriptionPaymentMethodStatus(state) {
            return state.currentSubscription?.paymentMethodStatus;
        },
        subscriptionCredit(state) {
            return state.subscriptionCredit || 0;
        },
        publicSponsorName(state) {
            return state.publicSponsorName || '';
        },
        paymentMode(state) {
            return state.paymentMode;
        },
        allowedCryptocurrencies(state) {
            return state.allowedCryptocurrencies;
        },
        allowedPaymentMethods(state) {
            return state.oneTimeDonationsAvailable || state.frontendMode === 'checkout' ? [...state.allowedPaymentMethods] : [];
        },
        thirdPartyPaymentMethods(state) {
            return [...state.thirdPartyPaymentMethods];
        },
        allowedEffects(state) {
            let allowed = {};
            for (let effect of state.effects) {
                if (state.allowedPaymentMethods.includes(state.paymentMode)) {
                    if (effect.minDonation && effect.donationEnabled) {
                        allowed[effect.id] = true;
                    }
                } else if (state.paymentMode == 'free') {
                    allowed[effect.id] = effect.effectType == 'preset' && state.freeGenerationEnabled || effect.effectType != 'preset' && state.freeEffectsEnabled;
                } else if (state.paymentMode == 'subscription') {
                    if ((state.authenticatedAsSubscriber && state.subscriber && state.subscriptionsEnabled && (state.limits[effect.cls] || (effect.effectType == 'preset' && state.freeGenerationSessionsLeft > 0))) || (state.authenticatedWithYoutube && state.sponsor && state.limits[effect.cls])) {
                        allowed[effect.id] = true;
                    }
                }
            }
            return allowed;
        },
        visuals(state) {
            return state.visuals;
        },
        selectedCryptoCurrencySymbol(state) {
            if (state.paymentMode in state.allowedCryptocurrencies) {
                return state.paymentMode;
            }
            return null;
        },
        channelCurrency(state) {
            return state.visuals.currency;
        },
        currencySymbol(state) {
            var currency = state.visuals.currency;
            if (currency == 'RUB') {
                return '₽';
            }
            if (currency == 'EUR') {
                return '€';
            }
            if (currency == 'USD') {
                return '$';
            }
            return currency;
        },
        effectPostInProgress(state) {
            return state.effectPostInProgress;
        },
        subscriptionUpdateTimerId(state) {
            return state.subscriptionUpdateTimerId;
        },
        paymentInProgress(state) {
            if( state.currentSubscription ) {
              return state.currentSubscription.inPayment;
            } else {
              return false;
            }
        },
        errorMessage(state) {
            return state.errorMessage;
        },
        paymentAddress(state) {
            return state.paymentAddress;
        },
        paymentAddressUUID(state) {
              return state.paymentAddressUUID;
        },
        paymentQRCode(state) {
            return state.paymentQRCode;
        },
        paymentURL(state) {
            var cc_params = state.allowedCryptocurrencies[state.paymentMode]
            if(cc_params && 'uri_template' in cc_params && state.paymentAddress && state.amount) {
              return cc_params['uri_template'].replace('${address}', state.paymentAddress).replace('${amount}', state.amount)
            }
            return null;
        },
        amount(state) {
            return state.amount;
        },
        cryptocurrencyPaymentPurpose(state) {
            return state.cryptocurrencyPaymentPurpose;
        },
        subscriptionPaymentIntent(state) {
            return state.subscriptionPaymentIntent;
        },
        giftPurchaseIntent(state) {
            return state.giftPurchaseIntent;
        },
        currentGiftPurchase(state) {
            return state.currentGiftPurchase;
        },
        currentGiftPurchases(state) {
            return state.currentGiftPurchases || [];
        },
        subscriptionRequestInProgress(state) {
          return state.subscriptionRequestInProgress;
        },
        downgradePlanned(state) {
          return state.currentSubscription?.nextLevel? state.currentSubscription.nextLevel.pricePerYearInUSD < state.currentSubscription.level.pricePerYearInUSD : false;
        },
        effectGenerationSession(state) {
          return state.effectGenerationSession;
        },
        availableEffectGenerationStylePresets(state) {
          return state.availableEffectGenerationStylePresets;
        },
        selectedEffectGenerationStylePresetID(state) {
          return state.selectedEffectGenerationStylePresetID;
        },
        maxGenerationAttempts(state) {
          return state.maxGenerationAttempts;
        },
        haveUnspentAttempts(state) {
          return state.effectGenerationSession?.attempts && (state.effectGenerationSession?.attempts?.length < state.maxGenerationAttempts || state.effectGenerationSession?.attempts?.some(attempt => attempt.status === EffectGenerationAttemptStatus.Created));
        },
        selectedAttemptIndex(state) {
          return state.selectedAttemptIndex;
        },
        effectGenerationSessionChecked(state) {
          return state.effectGenerationSessionChecked;
        },
        nameAllowedInGeneration(state) {
          const effectPreset = state.effects.find(effect => effect.effectType === 'preset');
          if (effectPreset) {
            return effectPreset.nameAllowed;
          }
          return false;
        },
        textAllowedInGeneration(state) {
          const effectPreset = state.effects.find(effect => effect.effectType === 'preset');
          if (effectPreset) {
            return effectPreset.textAllowed;
          }
          return false;
        },
        attemptSelectedBySponsor(state) {
          return currentAttempt(state)?.status === EffectGenerationAttemptStatus.EffectSelectedBySponsor;
        },
        newAttemptSelected(state) {
          const attemptStatus = currentAttempt(state)?.status;
          return attemptStatus === EffectGenerationAttemptStatus.Created || attemptStatus === EffectGenerationAttemptStatus.Processing;
        },
        currentAttemptStatus(state) {
          return currentAttempt(state)?.status;
        },
        generationInProgress(state) {
          return state.generationInProgress;
        },
        currentAttempt(state) {
          return currentAttempt(state);
        },
        generationDisabledGlobally(state) {
          return state.generationDisabledGlobally;
        },
        freeEffectsEnabled(state) {
          return state.freeEffectsEnabled;
        },
        freeEffectsPaymentOptionName(state) {
          return state.freeEffectsPaymentOptionName;
        },
        freeGenerationEnabled(state) {
          return state.freeGenerationEnabled;
        },
        freeGenerationSessionsLeft(state) {
          return state.freeGenerationSessionsLeft;
        },
        secondsUntilNextFreeEffect(state) {
          return state.secondsUntilNextFreeEffect;
        },
        googleRecaptchaSiteKey(state) {
          return state.googleRecaptchaSiteKey;
        },
        subscribersStats(state) {
          return state.subscribersStats || null;
        },
        allowedCryptoSymbols(state) {
          return Object.keys(state.allowedCryptocurrencies || {});
        },
        subscriptionPeriods(state) {
          return state.subscriptionPeriods || [1];
        },
        subscriptionDiscountsEnabled(state) {
          return !!state.subscriptionDiscountsEnabled;
        },
        generationAttempts(state) {
          return state.generationAttempts;
        },
        availablePeriodsForLevel: (state, getters) => (level) => {
          const base = Array.isArray(state.subscriptionPeriods) && state.subscriptionPeriods.length ? state.subscriptionPeriods : [1];
          const periods = new Set(base);
          if (getters.subscriptionDiscountsEnabled && level) {
            if (level.discount3m > 0) periods.add(3);
            if (level.discount6m > 0) periods.add(6);
            if (level.discount12m > 0) periods.add(12);
          }
          return Array.from(periods).sort((a, b) => a - b);
        },
        discountedPrice: (state, getters) => (level, months = 1) => {
          if (!level) return 0;
          if (months <= 0) months = 1;
          let price = level.price * months;
          if (!getters.subscriptionDiscountsEnabled) {
            return Math.round(price * 100) / 100;
          }
          let discount = 0;
          if (months === 3) discount = level.discount3m || 0;
          if (months === 6) discount = level.discount6m || 0;
          if (months === 12) discount = level.discount12m || 0;
          discount = Math.min(Math.max(discount, 0), 75);
          price = price * (1 - discount / 100);
          return Math.round(price * 100) / 100;
        },
        // Live flag getter for new design and views
        isLive(state) {
          return state.isLive;
        },
        // Whether Effects tab should be visible at all
        effectsTabAvailable(state, getters) {
          if (state.visuals?.effectsSelectionMode === 'disabled') {
            return !!state.oneTimeDonationsAvailable;
          }
          const donations = !!state.oneTimeDonationsAvailable;
          const free = !!(state.freeEffectsEnabled || state.freeGenerationEnabled);
          const subsFree = !!(state.subscriptionsEnabled && getters.freeEffectsAvailable);
          return donations || free || subsFree;
        },
        postsPagination(state) {
          return state.postsPagination || { nextPageToken: null, loading: false };
        },
        // Whether channel has any subscription-based limits (classic or AI generation)
        hasSubscriptionLimits(state, getters) {
          const levels = getters.sponsorshipLevels || [];
          for (const lvl of levels) {
            const hasGrants = Array.isArray(lvl.grants) && lvl.grants.length > 0;
            const hasGen = (lvl.freeGenerationSessions || 0) > 0;
            if (hasGrants || hasGen) return true;
          }
          return false;
        }
    },
    mutations: {
        // Wall and posts
        setWallEnabled(state, flag) { state.wallEnabled = !!flag; },
        setWallEnabledGlobal(state, flag) { state.wallEnabledGlobal = !!flag; },
        setWallEnabledChannel(state, flag) { state.wallEnabledChannel = !!flag; },
        setNewDesignEnabled(state, flag) {
            state.newDesignEnabled = !!flag;
        },
        setFrontendMode(state, mode) {
            state.frontendMode = mode || 'stream';
        },
        setCheckoutMinAmount(state, amount) {
            state.checkoutMinAmount = Number(amount) || 0;
        },
        setPosts(state, posts) {
          const arr = Array.isArray(posts) ? posts : [];
          const list = arr.map(p => new WallPost(p));
          state.posts = list;
          const idx = {};
          for (const p of list) { if (p && p.id != null) idx[String(p.id)] = true; }
          state.postsIndex = idx;
        },
        appendPosts(state, posts) {
          const arr = Array.isArray(posts) ? posts : [];
          const prev = state.posts || [];
          const out = [];
          for (const raw of arr) {
            const p = new WallPost(raw);
            const key = p && p.id != null ? String(p.id) : null;
            if (key && !state.postsIndex[key]) {
              state.postsIndex[key] = true;
              out.push(p);
            }
          }
          if (out.length) state.posts = [...prev, ...out];
        },
        setPostsPagination(state, payload) {
            state.postsPagination = { ...(state.postsPagination || {}), ...(payload || {}) };
        },
        selectEffect(state, id) {
            state.selectedEffect = id;
        },
        setCryptocurrencyPaymentMode(state, mode) {
            state.cryptocurrencyPaymentMode = mode;
        },
        previewEffect(state, id) {
            state.previewedEffect = id;
        },
        setStream(state, flag) {
            state.stream = flag;
        },
        setIsLive(state, flag) {
            state.isLive = flag;
        },
        setMode(state, mode) {
            state.mode = mode;
        },
        setTotal(state, value) {
            state.total = value;
        },
        setEffects(state, value) {
            state.effects = value || [];
            let oneTimeDonationsAvailable = false;
            for (let effect of state.effects) {
              if (effect.minDonation && effect.donationEnabled) {
                oneTimeDonationsAvailable = true;
                break;
              }
            }
            state.oneTimeDonationsAvailable = oneTimeDonationsAvailable || state.visuals.effectsSelectionMode != "manual";
        },
        setSponsor(state, value) {
            state.sponsor = value || false;
        },
        setSubscriber(state, value) {
            state.subscriber = value || false;
        },
        setJoinUrl(state, value) {
            state.joinUrl = value || '';
        },
        setLimits(state, value) {
            state.limits = value || {};
        },
        setEffectClasses(state, value) {
            state.effectClasses = value || [];
        },
        setAllowedCryptocurrencies(state, value) {
            state.allowedCryptocurrencies = value;
        },
        setAllowedPaymentMethods(state, value) {
            state.allowedPaymentMethods = value;
        },
        setVisuals(state, value) {
            const visuals = value || {};
            state.visuals = visuals;
            state.subscriptionModeSelectorVisible = visuals.effectsSelectionMode != "disabled" && (visuals.youtubeMembership || state.subscriptionsEnabled);
            state.total = visuals.defaultDonation;
            state.thirdPartyPaymentMethods = visuals.thirdPartyPaymentMethods || [];
            state.youtubeMembershipFreeEffectsAvailable = visuals.youtubeMembership;
        },
        setSubscriptionFreeEffectsIndex(state, value) {
            state.subscriptionFreeEffectsIndex = value || {};
        },
        setAuthenticatedWithYoutube(state, value) {
            state.authenticatedWithYoutube = value;
        },
        setAuthenticatedAsSubscriber(state, value) {
            state.authenticatedAsSubscriber = value;
        },
        setSubscriptionsEnabled(state, value) {
            state.subscriptionsEnabled = value;
            // Recompute visibility of subscription mode selector when subscriptions flag changes
            const v = state.visuals || {};
            state.subscriptionModeSelectorVisible = v.effectsSelectionMode != "disabled" && (v.youtubeMembership || state.subscriptionsEnabled);
        },
        setYoutubeMembershipFreeEffectsAvailable(state, value) {
            state.youtubeMembershipFreeEffectsAvailable = value;
        },
        setSponsorshipLevels(state, value) {
            let levels = [];
            value?.forEach((level) => {
                levels.push(new SponsorshipLevel(level));
            });
            state.sponsorshipLevels = levels;
        },
        setSelectedLevelID(state, value) {
            state.selectedLevelID = value;
        },
        setCurrentSubscription(state, value) {
            if(value != null) {
              const subscription = new Subscription(value);
              if(subscription.lastFailedPaymentAttempt) {
                state.errorMessage = i18n.t('subscriptions.payment_failed_try_again');
              }
              state.currentSubscription = subscription;
              if (subscription.status === SubscriptionStatus.Paid && subscription.paymentMethodStatus !== SubscriptionPaymentMethodStatus.Initiated) {
                state.selectedLevelID = subscription.level.externalID;
              } else {
                state.selectedLevelID = null;
              }
            } else {
              state.currentSubscription = null;
              state.selectedLevelID = null;
              state.pendingUpgradeInfo = null;
            }
        },
        setSubscriptionCredit(state, value) {
            state.subscriptionCredit = Number(value) || 0;
        },
        setPaymentMode(state, value) {
            state.paymentMode = value;
        },
        setEffectPostInProgress(state, value) {
            state.effectPostInProgress = value;
        },
        setSubscriptionUpdateTimerId(state, value) {
          state.subscriptionUpdateTimerId = value;
        },
        setErrorMessage(state, value) {
          state.errorMessage = value;
        },
        setErrorTimer(state, timer) {
          state.errorTimer = timer;
        },
        clearErrorTimer(state) {
          if (state.errorTimer) {
            clearTimeout(state.errorTimer);
            state.errorTimer = null;
          }
        },
        setPaymentData(state, data) {
            state.paymentAddress = data.address;
            state.paymentAddressUUID = data.address_uuid;
            state.paymentQRCode = data.qr_code;
            state.pendingCryptoPayment = data || null;
        },
        setPendingUpgradeInfo(state, info) {
            state.pendingUpgradeInfo = normalizePendingInfo(info);
        },
        setPublicNameLoaded(state, value) {
            state.publicNameLoaded = !!value;
        },
        setCryptocurrencyPaymentPurpose(state, purpose) {
            state.cryptocurrencyPaymentPurpose = purpose;
        },
        setSubscriptionPaymentIntent(state, intent) {
            state.subscriptionPaymentIntent = intent;
        },
        setGiftPurchaseIntent(state, intent) {
            state.giftPurchaseIntent = intent;
        },
        setCurrentGiftPurchase(state, purchase) {
            state.currentGiftPurchase = purchase || null;
        },
        setCurrentGiftPurchases(state, purchases) {
            state.currentGiftPurchases = Array.isArray(purchases) ? purchases : [];
        },
        setAmount(state, amount) {
            state.amount = amount;
        },
        setRateMultipliers(state, rateMultipliers) {
            state.rateMultipliers = rateMultipliers;
        },
        setSubscriptionRequestInProgress(state, value) {
          state.subscriptionRequestInProgress = value;
        },
        setEffectGenerationSession(state, session) {
          state.effectGenerationSession = session;
        },
        setEffectGenerationSessionChecked(state, value) {
          state.effectGenerationSessionChecked = value;
        },
        setAvailableEffectGenerationStylePresets(state, stylePresets, defaultPresetID = null) {
          let presets = [];
          stylePresets.forEach((preset) => {
            presets.push(new EffectGenerationStylePreset(preset));
          });
          state.availableEffectGenerationStylePresets = presets;
        },
        setSelectedEffectGenerationStylePresetID(state, defaultPresetID) {
          state.selectedEffectGenerationStylePresetID = defaultPresetID;
        },
        setMaxGenerationAttempts(state, maxGenerationAttempts) {
          state.maxGenerationAttempts = maxGenerationAttempts;
        },
        updateAttempt(state, attempt) {
          const index = state.effectGenerationSession?.attempts?.findIndex(a => a.uuid === attempt.uuid);
          if (index === null || index === undefined || index === -1) {
            logError('Attempt not found in session', attempt);
            return;
          }
          state.effectGenerationSession.attempts[index] = attempt;
          if (attempt.status === EffectGenerationAttemptStatus.Processing || attempt.status === EffectGenerationAttemptStatus.Created || state.effectGenerationSession.attempts.length >= state.maxGenerationAttempts) {
            return;
          }
          state.effectGenerationSession.attempts?.push(new EffectGenerationAttempt({status: EffectGenerationAttemptStatus.Created, uuid: getUUID()}));
        },
        setCurrentAttemptIndex(state, index) {
          state.selectedAttemptIndex = index;
        },
        selectAttempt(state) {
          state.selectedAttemptStatus = state.effectGenerationSession.attempts[state.selectedAttemptIndex].status;
          state.effectGenerationSession.attempts[state.selectedAttemptIndex].status = EffectGenerationAttemptStatus.EffectSelectedBySponsor;
        },
        cancelSelection(state) {
          state.effectGenerationSession.attempts[state.selectedAttemptIndex].status = state.selectedAttemptStatus;
          state.selectedAttemptStatus = null;
        },
        setGenerationInProgress(state, value) {
          state.generationInProgress = value;
        },
        setGenerationDisabledGlobally(state, value) {
          state.generationDisabledGlobally = value;
        },
        setGenerationStatusPollTimerId(state, value) {
          state.generationStatusPollTimerId = value;
        },
        setFreeEffectsEnabled(state, value) {
          state.freeEffectsEnabled = value;
        },
        setFreeGenerationEnabled(state, value) {
          state.freeGenerationEnabled = value;
        },
        setFreeGenerationSessionsLeft(state, value) {
          state.freeGenerationSessionsLeft = value || 0;
        },
        setFreeEffectsPaymentOptionName(state, value) {
          state.freeEffectsPaymentOptionName = value;
        },
        setSecondsUntilNextFreeEffect(state, value) {
          state.secondsUntilNextFreeEffect = value;
        },
        setSecondsUntilNextFreeEffectIntervalID(state, value) {
          state.secondsUntilNextFreeEffectIntervalID = value;
        },
        setGoogleRecaptchaSiteKey(state, value) {
          state.googleRecaptchaSiteKey = value;
        },
        setSubscribersStats(state, value) {
          state.subscribersStats = value || null;
        },
        setPublicSponsorName(state, value) {
          state.publicSponsorName = (value || '').toString();
        },
        setPublicNameSaveInProgress(state, value) {
          state.publicNameSaveInProgress = !!value;
        },
        setSubscriptionPeriods(state, periods) {
          if (Array.isArray(periods) && periods.length) {
            state.subscriptionPeriods = periods;
          }
        },
        setSubscriptionDiscountsEnabled(state, value) {
          state.subscriptionDiscountsEnabled = !!value;
        },
        setGenerationAttempts(state, value) {
          if (value && typeof value === 'object') {
            state.generationAttempts = { ...state.generationAttempts, ...value };
          }
        },
    },
    actions: {
        init(ctx, config) {
            // Feature flags and initial posts
            ctx.commit('setFrontendMode', config.frontend_mode || 'stream');
            ctx.commit('setCheckoutMinAmount', config.checkout_min_amount || 0);
            ctx.commit('setNewDesignEnabled', !!config.new_design_enabled);
            ctx.commit('setWallEnabled', !!config.wall_enabled);
            if (config.wall_enabled_global !== undefined) ctx.commit('setWallEnabledGlobal', !!config.wall_enabled_global);
            if (config.wall_enabled_channel !== undefined) ctx.commit('setWallEnabledChannel', !!config.wall_enabled_channel);
            if (config.posts) {
                ctx.commit('setPosts', config.posts);
            }
            if (config.posts_next_cursor !== undefined) {
                ctx.commit('setPostsPagination', { nextPageToken: config.posts_next_cursor || null });
            }
            ctx.commit('setVisuals', config.visuals);
            ctx.commit('setJoinUrl', config.join_url);
            ctx.commit('setEffects', config.effects);
            if(config.visuals?.effectsSelectionMode === 'manual' && config.effects?.[0]?.id) {
                ctx.commit('selectEffect', config.effects[0].id);
            }
            ctx.commit('setAuthenticatedWithYoutube', config.authenticated_with_youtube);
            ctx.commit('setAuthenticatedAsSubscriber', config.authenticated_as_subscriber);
            ctx.commit('setSubscriptionFreeEffectsIndex', config.subscription_free_effects_index || {});
            ctx.commit('setSubscriptionsEnabled', config.subscriptions_enabled);
            ctx.commit('setSponsorshipLevels', config.sponsorship_levels || []);
            ctx.commit('setCurrentSubscription', config.subscription);
            if (typeof config.subscription_credit === 'number') {
                ctx.commit('setSubscriptionCredit', config.subscription_credit);
            }
            if(config.subscription?.in_payment && !config.pending_info && !config.subscription?.next_level) {
                ctx.dispatch('startSubscriptionUpdate');
            }
            ctx.commit('setGenerationDisabledGlobally', config.generation_disabled_globally);
            ctx.commit('setSponsor', config.sponsor);
            ctx.commit('setSubscriber', config.subscriber);
            ctx.commit('setLimits', config.limits);
            ctx.commit('setEffectClasses', config.effect_classes || []);
            ctx.commit('setSelectedEffectGenerationStylePresetID', config.default_effect_generation_style_preset_id || null)
            ctx.commit('setAvailableEffectGenerationStylePresets', config.enabled_effect_generation_style_presets || []);
            ctx.commit('setMaxGenerationAttempts', config.max_generation_attempts || 3);
            if (config.generation_attempts) {
              ctx.commit('setGenerationAttempts', config.generation_attempts);
            }
            if (config.pending_info) {
                ctx.commit('setPendingUpgradeInfo', config.pending_info);
            }
            if (config.pending_crypto_payment) {
                ctx.commit('setPaymentData', {
                    address: config.pending_crypto_payment.address,
                    address_uuid: config.pending_crypto_payment.address_uuid,
                    qr_code: config.pending_crypto_payment.qr_code,
                });
                ctx.commit('setAmount', config.pending_crypto_payment.amount);
                ctx.commit('setCryptocurrencyPaymentMode', 'awaiting_payment');
                ctx.commit('setCryptocurrencyPaymentPurpose', 'subscription');
                if (config.pending_crypto_payment.payment_currency) {
                  ctx.commit('setPaymentMode', config.pending_crypto_payment.payment_currency);
                }
                if (isNewDesignActive(ctx)) {
                  router.replace('/wall/subscription/cryptopayment').catch((err) => {
                    if (!err || err.name !== 'NavigationDuplicated') {
                      logWarn('Failed to navigate to subscription crypto payment from init', err);
                    }
                  });
                }
                ctx.dispatch('updateCryptocurrencyPaymentStatus');
            }
            // visuals already set above
            ctx.commit('setAllowedCryptocurrencies', config.allowed_cryptocurrencies || {});
            ctx.commit('setAllowedPaymentMethods', config.allowed_payment_methods || []);
            ctx.commit('setSubscriptionDiscountsEnabled', !!config.subscription_discounts_enabled);
            if (config.subscription_periods) {
              ctx.commit('setSubscriptionPeriods', config.subscription_periods);
            }
            ctx.commit('setRateMultipliers', config.currency_rate_multipliers);
            ctx.commit('setSubscribersStats', config.subscribers_stats || null);
            if (!ctx.state.cryptocurrencyPaymentMode) {
              let defaultMode = config.allowed_payment_methods ? (config.allowed_payment_methods.includes('donation') ? 'donation' : config.allowed_payment_methods[0]) : null;
              // Determine if channel has any subscription-based limits (classic or AI)
              const levels = config.sponsorship_levels || [];
              const grantsIndex = config.subscription_free_effects_index || {};
              let hasSubsLimits = false;
              for (const lvl of levels) {
                const grants = grantsIndex?.[lvl.external_id] || [];
                if ((Array.isArray(grants) && grants.length > 0) || ((lvl.free_generation_sessions || 0) > 0)) { hasSubsLimits = true; break; }
              }
              let targetMode = defaultMode;
              if(config.visuals?.effectsSelectionMode == "disabled") {
                  targetMode = defaultMode;
              } else if(((config.subscriptions_enabled && config.authenticated_as_subscriber) || (config.authenticated_with_youtube && (!config.sponsor || ctx.getters.freeEffectsAvailable) && config.visuals?.youtubeMembership))) {
                  // If a user authenticates with YouTube not being a sponsor, we select YouTube
                  // mode nevertheless, because it shows important information on how to become a sponsor.
                  targetMode = 'subscription';
              } else if((config.subscriptions_enabled || config.visuals?.youtubeMembership) && !ctx.getters.oneTimeDonationsAvailable) {
                  targetMode = 'subscription';
              } else {
                  targetMode = defaultMode;
              }
              // If subscription mode was selected but channel has no subscription limits, fall back to default (donation)
              if (targetMode === 'subscription' && !hasSubsLimits) {
                targetMode = defaultMode;
              }
              ctx.commit('setPaymentMode', targetMode);
            }
            ctx.dispatch('validateEffect');
            if (config.effect_generation_session) {
                ctx.dispatch('checkEffectGenerationSession', config.effect_generation_session);
            }
            if (config.free_effects_enabled || config.free_generation_enabled) {
                ctx.commit('setFreeEffectsEnabled', config.free_effects_enabled);
                ctx.commit('setFreeGenerationEnabled', config.free_generation_enabled);
                ctx.commit('setFreeEffectsPaymentOptionName', config.visuals?.freeEffectsPaymentOptionName);
                ctx.dispatch('setSecondsUntilNextFreeEffect', config.seconds_until_next_free_effect);
                ctx.commit('setGoogleRecaptchaSiteKey', config.google_recaptcha_site_key);
            }
            ctx.dispatch('setFreeGenerationSessionsLeft', config.remaining_generation_sessions || 0);
            ctx.commit('setIsLive', config.is_live);
        },
        async fetchPublicSponsorName(ctx) {
            if (ctx.state.publicNameLoaded) return;
            try {
                const resp = await GetPublicSponsorName();
                if (resp?.data?.public_name !== undefined) {
                    ctx.commit('setPublicSponsorName', resp.data.public_name);
                }
                ctx.commit('setPublicNameLoaded', true);
            } catch (e) {
                // 404 means no subscription yet; ignore
                if (!e || !e.response || e.response.status !== 404) {
                  logWarn('Failed to fetch public sponsor name', e);
                }
            }
        },
        async savePublicSponsorName(ctx, name) {
            ctx.commit('setPublicNameSaveInProgress', true);
            try {
                const resp = await SetPublicSponsorName(name || '');
                if (resp?.data?.success) {
                    ctx.commit('setPublicSponsorName', resp.data.public_name || '');
                }
            } finally {
                ctx.commit('setPublicNameSaveInProgress', false);
            }
        },
        // Infinite scroll: load more wall posts
        async loadMorePosts(ctx) {
            if (!ctx.state.wallEnabled || ctx.state.postsPagination.loading) return;
            if (!ctx.state.postsPagination.nextPageToken) return; // nothing more
            ctx.commit('setPostsPagination', { loading: true });
            try {
                const resp = await GetWallPosts(ctx.state.postsPagination.nextPageToken);
                const posts = resp.data?.posts || [];
                if (posts.length) {
                    ctx.commit('appendPosts', posts);
                }
                const next = resp.data?.next_cursor || null;
                ctx.commit('setPostsPagination', { nextPageToken: next || null, loading: false });
            } catch (e) {
                logWarn('Error loading more posts', e);
                ctx.commit('setPostsPagination', { loading: false });
            }
        },
        checkEffectGenerationSession(ctx, uuid) {
            let future = GetEffectGenerationSession(uuid);
            future.then(
                (response) => {
                    if (response.data) {
                        const previousSessionExists = !!ctx.state.effectGenerationSession;
                        const previousIndex = ctx.state.selectedAttemptIndex;
                        let session = new EffectGenerationSession(response.data);
                        // Adjust max attempts based on session type and user status
                        let limit = ctx.state.generationAttempts.donation || ctx.state.maxGenerationAttempts;
                        if (session.isFree) {
                          limit = ctx.state.generationAttempts.free || limit;
                        } else if (ctx.state.subscriber && ctx.state.subscriptionsEnabled) {
                          limit = ctx.state.generationAttempts.subscription || limit;
                        }
                        ctx.commit('setMaxGenerationAttempts', limit || ctx.state.maxGenerationAttempts);
                        if (!session.isExpired) {
                          const hasProcessingAttempt = session.attempts.some(a => a.status === EffectGenerationAttemptStatus.Processing);
                          if (session.attempts.length < ctx.state.maxGenerationAttempts && !hasProcessingAttempt) {
                            session.attempts.push(new EffectGenerationAttempt({status: EffectGenerationAttemptStatus.Created, uuid: getUUID()}));
                          }
                          ctx.commit('setEffectGenerationSession', session);
                          const indexOutOfRange = previousIndex == null || previousIndex >= session.attempts.length || previousIndex < 0;
                          const nextIndex = !previousSessionExists || indexOutOfRange ? session.attempts.length - 1 : previousIndex;
                          ctx.commit('setCurrentAttemptIndex', nextIndex);
                          ctx.commit('setGenerationInProgress', hasProcessingAttempt);
                          if (hasProcessingAttempt) {
                            ctx.dispatch('startGenerationStatusPolling');
                          } else {
                            ctx.dispatch('stopGenerationStatusPolling');
                          }
                        } else {
                          ctx.commit('setEffectGenerationSession', null);
                          ctx.commit('setGenerationInProgress', false);
                          ctx.dispatch('stopGenerationStatusPolling');
                        }
                        ctx.commit('setEffectGenerationSessionChecked', true);
                    } else {
                      logError('No data in response while checking effect generation session');
                      ctx.dispatch('setErrorMessage', i18n.t('error.error_while_checking_sticker_creation_session_please_reload'));
                    }
                },
                (error) => {
                  logError('Error while checking effect generation session', error);
                  ctx.dispatch('setErrorMessage', i18n.t('error.error_while_checking_sticker_creation_session_please_reload'));
                }
            );
            return future;
        },
        returnToCryptoPaymentForGenerationSession(ctx) {
          let paymentData = ctx.state.effectGenerationSession?.cryptoPaymentDetails;
          if (paymentData) {
            ctx.dispatch('setPaymentMode', paymentData.currency)
            ctx.dispatch('startCryptoPayment', paymentData);
          }
        },
        stopGenerationStatusPolling(ctx) {
          if (ctx.state.generationStatusPollTimerId !== null) {
            clearTimeout(ctx.state.generationStatusPollTimerId);
            ctx.commit('setGenerationStatusPollTimerId', null);
          }
        },
        startGenerationStatusPolling(ctx) {
          if (!ctx.state.effectGenerationSession?.uuid) {
            return;
          }
          if (ctx.state.generationStatusPollTimerId !== null) {
            return;
          }
          ctx.commit('setGenerationStatusPollTimerId', -1);
          const poll = () => {
            ctx.dispatch('checkEffectGenerationSession', ctx.state.effectGenerationSession.uuid).finally(() => {
              const hasProcessingAttempt = ctx.state.effectGenerationSession?.attempts?.some(a => a.status === EffectGenerationAttemptStatus.Processing);
              if (hasProcessingAttempt) {
                const timerId = setTimeout(poll, 2000);
                ctx.commit('setGenerationStatusPollTimerId', timerId);
              } else {
                ctx.dispatch('stopGenerationStatusPolling');
              }
            });
          };
          poll();
        },
        validateEffect(ctx) {
            let selected = ctx.state.selectedEffect;
            let allowed = ctx.getters.allowedEffects;
            if (selected && allowed[selected]) {
                if (ctx.state.allowedPaymentMethods.includes(ctx.state.paymentMode)) {
                    let minDonation = ctx.getters.effectIndex[selected].minDonation;
                    if ((ctx.state.total < minDonation) || ctx.state.visuals.autoReduceDonation) {
                        ctx.commit('setTotal', minDonation);
                    }
                }
                return;
            }
            for (let effect of ctx.state.effects) {
                if (!allowed[effect.id]) {
                    continue;
                }
                if (ctx.state.allowedPaymentMethods.includes(ctx.state.paymentMode) && self.total < effect.minDonation) {
                    continue;
                }
                ctx.commit('selectEffect', effect.id);
                return;
            }
            ctx.commit('selectEffect', null);
        },
        validateAmount(ctx) {
        },
        setSelectedEffectGenerationStylePresetID(ctx, styleID) {
          ctx.commit('setSelectedEffectGenerationStylePresetID', styleID);
        },
        selectEffect(ctx, id) {
            ctx.commit('selectEffect', id);
            ctx.dispatch('validateEffect');
        },
        previewEffect(ctx, id) {
            ctx.commit('previewEffect', id);
        },
        setTotal(ctx, val) {
            // To make sure input is updated.
            ctx.commit('setTotal', undefined);
            ctx.commit('setTotal', val);
            ctx.dispatch('validateAmount');
        },
        setPaymentMode(ctx, mode) {
            ctx.commit('setPaymentMode', mode);
            ctx.dispatch('validateEffect');
        },
        setSelectedLevelID(ctx, id) {
            ctx.commit('setSelectedLevelID', id);
        },
        setLimits(ctx, limits) {
            ctx.commit('setLimits', limits);
            ctx.dispatch('validateEffect');
        },
        setFreeGenerationSessionsLeft(ctx, value) {
            ctx.commit('setFreeGenerationSessionsLeft', value);
            ctx.dispatch('validateEffect');
        },
        setEffectPostInProgress(ctx, value) {
            ctx.commit('setEffectPostInProgress', value);
        },
        setErrorMessage(ctx, value) {
          ctx.commit('clearErrorTimer');
          ctx.commit('setErrorMessage', value);
          if (value) {
            ctx.commit('setErrorTimer', setTimeout(() => {ctx.commit('setErrorMessage', null)}, 15000));
          }
        },
        startCryptoPayment(ctx, payment_data) {
            const purpose = payment_data.kind || ctx.state.cryptocurrencyPaymentPurpose || (ctx.state.subscriptionPaymentIntent ? 'subscription' : 'donation');
            ctx.commit('setPaymentData', payment_data);
            if (payment_data.payment_currency) {
              ctx.commit('setPaymentMode', payment_data.payment_currency);
            }
            ctx.commit('setAmount', payment_data.amount);
            ctx.commit('setCryptocurrencyPaymentMode', 'awaiting_payment');
            ctx.commit('setCryptocurrencyPaymentPurpose', purpose);
            ctx.commit('setPendingUpgradeInfo', payment_data.pending_info || ctx.state.pendingUpgradeInfo || null);
            if (ctx.getters.checkoutFrontendEnabled || isNewDesignActive(ctx)) {
              let target;
              if (ctx.getters.checkoutFrontendEnabled && purpose === 'donation') {
                target = '/checkout/cryptopayment';
              } else {
                target = (purpose === 'subscription' || purpose === 'subscription_gift') ? '/wall/subscription/cryptopayment' : '/wall/cryptopayment';
              }
              if (router.currentRoute.path !== target) {
                router.push(target).catch((err) => {
                  if (!err || err.name !== 'NavigationDuplicated') {
                    logWarn('Failed to navigate to crypto payment page', err);
                  }
                });
              }
            }
            ctx.dispatch('updateCryptocurrencyPaymentStatus');
        },
        returnToSubscriptionFromCrypto(ctx) {
            if (ctx.state.cryptocurrencyPaymentPurpose === 'subscription_gift') {
                ctx.commit('setCryptocurrencyPaymentPurpose', null);
                ctx.commit('setCryptocurrencyPaymentMode', null);
                ctx.commit('setPaymentData', { address: null, address_uuid: null, qr_code: null });
                ctx.commit('setAmount', null);
                router.push('/wall/subscription').catch((err) => {
                  if (!err || err.name !== 'NavigationDuplicated') {
                    logWarn('Failed to navigate back to subscription from gift crypto', err);
                  }
                });
                return;
            }
            const info = ctx.state.pendingUpgradeInfo;
            const prevIntent = ctx.state.subscriptionPaymentIntent;
            const levels = ctx.getters.sponsorshipLevels || [];
            let targetLevel = null;
            if (info && info.nextLevelName) {
                targetLevel = levels.find(l => l.name === info.nextLevelName) || null;
            }
            const sub = ctx.state.currentSubscription;
            if (!targetLevel && sub && sub.nextLevel) {
                targetLevel = sub.nextLevel;
            }
            if (!targetLevel && sub && sub.level) {
                targetLevel = sub.level;
            }
            if (targetLevel && targetLevel.ID) {
                const months = prevIntent && prevIntent.months ? prevIntent.months : 1;
                const paymentCurrency = prevIntent && prevIntent.paymentCurrency ? prevIntent.paymentCurrency : null;
                ctx.commit('setSubscriptionPaymentIntent', {
                    levelId: targetLevel.ID,
                    months,
                    paymentCurrency,
                });
            } else {
                ctx.commit('setSubscriptionPaymentIntent', null);
            }
            ctx.commit('setCryptocurrencyPaymentPurpose', null);
            ctx.commit('setCryptocurrencyPaymentMode', null);
            ctx.commit('setPaymentData', { address: null, address_uuid: null, qr_code: null });
            ctx.commit('setAmount', null);
            router.push('/wall/subscription').catch((err) => {
              if (!err || err.name !== 'NavigationDuplicated') {
                logWarn('Failed to navigate back to subscription from crypto', err);
              }
            });
        },
        cancelPendingSubscription(ctx) {
            ctx.commit('setSubscriptionRequestInProgress', true);
            return CancelPendingSubscription().then(() => {
                ctx.commit('setPendingUpgradeInfo', null);
                ctx.commit('setCryptocurrencyPaymentMode', null);
                ctx.commit('setCryptocurrencyPaymentPurpose', null);
                ctx.commit('setPaymentData', { address: null, address_uuid: null, qr_code: null });
                ctx.commit('setAmount', null);
                ctx.dispatch('updateSubscription');
                // After cancelling or completing crypto upgrade, reload wall posts to reflect new access.
                ctx.dispatch('reloadWallPosts');
                router.push('/wall/subscription');
            }).finally(() => {
                ctx.commit('setSubscriptionRequestInProgress', false);
            });
        },
        setEffectGenerationSession(ctx, session) {
            ctx.commit('setEffectGenerationSession', session);
        },
        updateCryptocurrencyPaymentStatus(ctx){
            if (!ctx.state.paymentAddressUUID || !ctx.state.cryptocurrencyPaymentMode) {
                return;
            }
            setTimeout(function() {
                if (!ctx.state.cryptocurrencyPaymentMode) {
                    return;
                }
                var future = GetCryptoPaymentStatus(ctx.state.paymentAddressUUID);
                future.then(function(response) {
                    var currMode = response.data.result
                    if (currMode == 'completed') {
                        const purpose = ctx.state.cryptocurrencyPaymentPurpose;
                        const giftIntent = ctx.state.giftPurchaseIntent;
                        ctx.commit('setCryptocurrencyPaymentMode', null)
                        if (purpose === 'subscription') {
                          ctx.commit('setPaymentMode', 'subscription');
                        } else if (purpose === 'subscription_gift') {
                          ctx.commit('setPaymentMode', 'subscription');
                        } else {
                          ctx.commit('setPaymentMode', 'donation');
                        }
                        if (purpose === 'subscription') {
                            ctx.dispatch('updateSubscription');
                            ctx.dispatch('reloadWallPosts');
                            ctx.commit('setSubscriptionPaymentIntent', null);
                            ctx.commit('setCryptocurrencyPaymentPurpose', null);
                            router.push('/wall/subscription').catch((err) => {
                              if (!err || err.name !== 'NavigationDuplicated') {
                                logWarn('Failed to navigate to subscription after crypto payment completion', err);
                              }
                            });
                        } else if (purpose === 'subscription_gift') {
                            const purchaseId = giftIntent?.purchaseId;
                            ctx.commit('setCryptocurrencyPaymentPurpose', null);
                            if (purchaseId) {
                              ctx.dispatch('fetchGiftSubscriptionPurchase', purchaseId).finally(() => {
                                router.push(`/wall/subscription?gift_purchase_id=${purchaseId}`).catch((err) => {
                                  if (!err || err.name !== 'NavigationDuplicated') {
                                    logWarn('Failed to navigate to subscription after gift crypto payment completion', err);
                                  }
                                });
                              });
                            } else {
                              router.push('/wall/subscription').catch((err) => {
                                if (!err || err.name !== 'NavigationDuplicated') {
                                  logWarn('Failed to navigate to subscription after gift crypto payment completion', err);
                                }
                              });
                            }
                        } else if (ctx.state.effectGenerationSession?.uuid) {
                            const sessionUUID = ctx.state.effectGenerationSession?.uuid;
                            router
                              .push(resolveDesignPath(ctx, `/generate?session=${sessionUUID}`, `/wall/generate?session=${sessionUUID}`))
                              .catch((err) => {
                                if (!err || err.name !== 'NavigationDuplicated') {
                                  logWarn('Failed to navigate to generation page after crypto payment completion', err);
                                }
                              });
                        } else {
                            const target = ctx.getters.checkoutFrontendEnabled ? '/checkout/success' : resolveDesignPath(ctx, '/sent', '/wall/sent');
                            router.push(target).catch((err) => {
                              if (!err || err.name !== 'NavigationDuplicated') {
                                logWarn('Failed to navigate to sent page after crypto payment completion', err);
                              }
                            });
                        }
                        return
                    }
                    if (ctx.state.cryptocurrencyPaymentMode) {
                        ctx.commit('setCryptocurrencyPaymentMode', currMode)
                        ctx.dispatch('updateCryptocurrencyPaymentStatus');
                    }
                }).catch(function(error) {
                    logError('Error getting cryptopayment status', error);
                    ctx.dispatch('updateCryptocurrencyPaymentStatus');
                });
            }, 5000)
        },
        updateCurrentSubscription(ctx, newSubscriptionData) {
            if(newSubscriptionData?.in_payment && !newSubscriptionData?.next_level) {
                ctx.dispatch('startSubscriptionUpdate');
            }
            ctx.commit('setCurrentSubscription', newSubscriptionData);
            const sub = ctx.state.currentSubscription;
            const isActiveSubscriber = !!(
              sub &&
              sub.status === SubscriptionStatus.Paid &&
              sub.paymentMethodStatus !== SubscriptionPaymentMethodStatus.Initiated
            );
            ctx.commit('setSubscriber', isActiveSubscriber);
        },
        unsubscribe(ctx) {
            ctx.commit('setSubscriptionRequestInProgress', true);
            let future = Unsubscribe();
            future.then(
              (response) => {
                ctx.dispatch('updateCurrentSubscription', response.data?.subscription);
                ctx.commit('setSubscriptionRequestInProgress', false);
                router.push(resolveDesignPath(ctx, '/subscriptions', '/wall/subscription')).catch((err) => {
                  if (!err || err.name !== 'NavigationDuplicated') {
                    logWarn('Failed to navigate to subscriptions after unsubscribe', err);
                  }
                });
              },
              (error) => {
                logError('Error while unsubscribing', error);
                if(error.response?.status === 409) {
                  ctx.dispatch('updateCurrentSubscription', error.response?.data?.subscription);
                }
                if (error.response?.data?.error_text) {
                  ctx.dispatch('setErrorMessage', error.response.data.error_text);
                } else {
                  ctx.dispatch('setErrorMessage', i18n.t('error.error_while_unsubscribing', { code: error.response?.status }));
                }
                ctx.commit('setSubscriptionRequestInProgress', false);
              }
            );
        },
        subscribe(ctx, levelId) {
          return ctx.dispatch('subscribeWithOptions', { levelId, months: 1 });
        },
        checkoutSubscribe(ctx, amount) {
          ctx.commit('setSubscriptionRequestInProgress', true);
          return CheckoutSubscribe(amount).then(
            (response) => {
              if (response.data?.error) {
                ctx.dispatch('setErrorMessage', response.data.error);
                ctx.commit('setSubscriptionRequestInProgress', false);
                return;
              }
              if (response.data?.redirect) {
                window.location.href = response.data.redirect;
                return;
              }
              if (response.data?.error_text) {
                ctx.dispatch('setErrorMessage', response.data.error_text);
              }
              ctx.dispatch('updateCurrentSubscription', response.data?.subscription);
              ctx.commit('setSubscriptionRequestInProgress', false);
              router.push('/checkout/subscription').catch((err) => {
                if (!err || err.name !== 'NavigationDuplicated') {
                  logWarn('Failed to navigate to checkout subscription after subscribe', err);
                }
              });
            },
            (error) => {
              if (error.response?.data?.redirect) {
                window.location.href = error.response.data.redirect;
                return;
              }
              if (error.response?.data?.error_text) {
                ctx.dispatch('setErrorMessage', error.response.data.error_text);
              } else {
                ctx.dispatch('setErrorMessage', i18n.t('error.error_while_subscribing', { code: error.response?.status }));
              }
              if (error.response?.data?.subscription) {
                ctx.dispatch('updateCurrentSubscription', error.response.data.subscription);
              }
              ctx.commit('setSubscriptionRequestInProgress', false);
            }
          );
        },
        subscribeWithOptions(ctx, { levelId, months = 1, method = 'card', paymentCurrency = null }) {
          ctx.commit('setSubscriptionRequestInProgress', true)
          if (months <= 0) months = 1;
          if (method === 'crypto') {
            if (!paymentCurrency) {
              const first = ctx.getters.allowedCryptoSymbols?.[0];
              paymentCurrency = first || 'BTC';
            }
            ctx.commit('setSubscriptionPaymentIntent', { levelId, months, paymentCurrency });
            return SubscribeCrypto(levelId, months, paymentCurrency).then(
              (response) => {
                if (response.data?.address) {
                  ctx.dispatch('startCryptoPayment', { ...response.data, kind: 'subscription' });
                } else {
                  // Free upgrade case: backend applied upgrade without issuing a crypto address.
                  ctx.dispatch('updateSubscription');
                  ctx.dispatch('reloadWallPosts');
                }
                ctx.commit('setSubscriptionRequestInProgress', false);
              },
              (error) => {
                logError('Error while subscribing with crypto', error);
                ctx.dispatch('setErrorMessage', i18n.t('error.error_while_subscribing', { code: error.response?.status }));
                ctx.commit('setSubscriptionRequestInProgress', false);
              }
            );
          }
          let future = Subscribe(levelId, months);
          future.then(
            (response) => {
              if (response.data?.redirect) {
                window.location.href = response.data.redirect;
                return
              } else if (response.data?.error_text) {
                ctx.dispatch('setErrorMessage', response.data.error_text);
              }
              ctx.dispatch('updateCurrentSubscription', response.data?.subscription);
              ctx.commit('setSubscriptionRequestInProgress', false);
              router.push(resolveDesignPath(ctx, '/subscriptions', '/wall/subscription')).catch((err) => {
                if (!err || err.name !== 'NavigationDuplicated') {
                  logWarn('Failed to navigate to subscriptions after subscribe', err);
                }
              });
            },
            (error) => {
              if (error.response?.data?.redirect) {
                window.location.href = error.response.data.redirect;
                return
              } else if (error.response?.data?.error_text) {
                ctx.dispatch('setErrorMessage', error.response.data.error_text);
              } else {
                ctx.dispatch('setErrorMessage', i18n.t('error.error_while_subscribing', { code: error.response?.status }));
              }
              if(error.response?.data?.subscription) {
                ctx.dispatch('updateCurrentSubscription', error.response.data.subscription);
                router.push(resolveDesignPath(ctx, '/subscriptions', '/wall/subscription')).catch((err) => {
                  if (!err || err.name !== 'NavigationDuplicated') {
                    logWarn('Failed to navigate to subscriptions after subscription error', err);
                  }
                });
              }
              ctx.commit('setSubscriptionRequestInProgress', false);
            }
          );
        },
        stopSubscriptionUpdate(ctx) {
            clearTimeout(ctx.state.subscriptionUpdateTimerId);
            ctx.commit('setSubscriptionUpdateTimerId', null);
        },
        startSubscriptionUpdate(ctx) {
            if(ctx.state.subscriptionUpdateTimerId != null) return;
            ctx.dispatch('updateSubscription');
        },
        updateSubscription(ctx) {
          let future = GetSubscription();
          future.then(
            (response) => {
              ctx.dispatch('updateCurrentSubscription', response.data?.subscription);
              if (typeof response.data?.subscription_credit === 'number') {
                ctx.commit('setSubscriptionCredit', response.data.subscription_credit || 0);
              }
              if (response.data?.limits) {
                ctx.dispatch('setLimits', response.data.limits);
              }
              if (typeof response.data?.remaining_generation_sessions === 'number') {
                ctx.dispatch('setFreeGenerationSessionsLeft', response.data.remaining_generation_sessions);
              }
              ctx.commit('setPendingUpgradeInfo', response.data?.pending_info || null);
              if(response.data?.error_text) {
                ctx.dispatch('setErrorMessage', response.data.error_text);
              }
              const sub = response.data?.subscription;
              const hasPendingUpgrade = !!(sub && sub.in_payment && sub.next_level);
              if (sub && !sub.in_payment) {
                ctx.dispatch('stopSubscriptionUpdate');
                // When subscription leaves in_payment state (payment finished or failed),
                // reload wall posts so locked content reflects current access.
                ctx.dispatch('reloadWallPosts');
              } else if (hasPendingUpgrade) {
                ctx.dispatch('stopSubscriptionUpdate');
              } else {
                ctx.commit('setSubscriptionUpdateTimerId', setTimeout(() => {
                  ctx.dispatch('updateSubscription');
                }, 5000));
              }
            },
            (error) => {
              if (error.response?.status && error.response.status < 500) {
                if (error.response?.data?.redirect) {
                  window.location.href = error.response?.data.redirect;
                  return
                } else if (error.response?.data?.error_text) {
                  ctx.dispatch('setErrorMessage', error.response.data.error_text);
                } else {
                  ctx.commit('setErrorMessage', null);
                }
                ctx.dispatch('stopSubscriptionUpdate');
              } else {
                ctx.commit('setSubscriptionUpdateTimerId', setTimeout(() => {
                  ctx.dispatch('updateSubscription');
                }, 5000));
              }
            }
          );
        },
        paySubscription(ctx, months = 1) {
            let future = PaySubscription(months);
            ctx.commit('setSubscriptionRequestInProgress', true)
            future.then(
              (response) => {
                ctx.dispatch('updateCurrentSubscription', response.data?.subscription);
                if (response.data?.redirect) {
                  window.location.href = response.data.redirect;
                  return
                } else if (response.data?.error_text) {
                  ctx.dispatch('setErrorMessage', response.data.error_text);
                } else {
                  ctx.commit('setErrorMessage', null);
                }
                ctx.commit('setSubscriptionRequestInProgress', false)
              },
              (error) => {
                if ([409, 402].includes(error.response?.status)) {
                  ctx.dispatch('updateCurrentSubscription', error.response?.data?.subscription);
                  ctx.dispatch('setErrorMessage', error.response?.data?.error_text ? error.response.data.error_text : i18n.t('error.error_while_payment'));
                } else {
                  logError('Error while paying for subscription', error);
                  ctx.dispatch('setErrorMessage', i18n.t('error.error_while_payment'));
                }
                ctx.commit('setSubscriptionRequestInProgress', false)
              }
            );
        },
        createGiftSubscriptionPurchase(ctx, { levelId, months = 1, quantity = 1, method = 'card', paymentCurrency = null }) {
            ctx.commit('setSubscriptionRequestInProgress', true);
            if (months <= 0) months = 1;
            if (quantity <= 0) quantity = 1;
            return CreateGiftSubscriptionPurchase(levelId, months, quantity, method, paymentCurrency).then((response) => {
                const purchaseId = response?.data?.purchase_id || response?.data?.purchase?.id || null;
                if (purchaseId) {
                    ctx.commit('setGiftPurchaseIntent', {
                        purchaseId,
                        levelId,
                        months,
                        quantity,
                        paymentCurrency: paymentCurrency || response?.data?.payment_currency || null,
                    });
                }
                if (response?.data?.purchase) {
                    ctx.commit('setCurrentGiftPurchase', response.data.purchase);
                    ctx.commit('setCurrentGiftPurchases', [response.data.purchase]);
                }
                if (response?.data?.redirect) {
                    window.location.href = response.data.redirect;
                    return response.data;
                }
                if (response?.data?.address_uuid) {
                    ctx.dispatch('startCryptoPayment', { ...response.data, kind: 'subscription_gift' });
                    return response.data;
                }
                if (purchaseId) {
                    return ctx.dispatch('fetchGiftSubscriptionPurchase', purchaseId);
                }
                return response.data;
            }).catch((error) => {
                if (error?.response?.data?.error_text) {
                    ctx.dispatch('setErrorMessage', error.response.data.error_text);
                } else {
                    ctx.dispatch('setErrorMessage', i18n.t('error.error_while_subscribing', { code: error?.response?.status }));
                }
                throw error;
            }).finally(() => {
                ctx.commit('setSubscriptionRequestInProgress', false);
            });
        },
        fetchGiftSubscriptionPurchase(ctx, purchaseId) {
            if (!purchaseId) return Promise.resolve(null);
            return GetGiftSubscriptionPurchase(purchaseId).then((response) => {
                const purchase = response?.data?.purchase || null;
                const purchases = response?.data?.purchases || (purchase ? [purchase] : []);
                ctx.commit('setCurrentGiftPurchase', purchase);
                ctx.commit('setCurrentGiftPurchases', purchases);
                if (purchase && purchase.status === 'paid') {
                    ctx.commit('setGiftPurchaseIntent', null);
                }
                return purchase;
            }).catch((error) => {
                logWarn('Failed to fetch gift subscription purchase', error);
                return null;
            });
        },
        fetchLatestGiftSubscriptionPurchase(ctx) {
            return GetGiftSubscriptionPurchase(null).then((response) => {
                const purchase = response?.data?.purchase || null;
                const purchases = response?.data?.purchases || (purchase ? [purchase] : []);
                ctx.commit('setCurrentGiftPurchase', purchase);
                ctx.commit('setCurrentGiftPurchases', purchases);
                return purchase;
            }).catch((error) => {
                logWarn('Failed to fetch latest gift subscription purchase', error);
                return null;
            });
        },
        previewGiftSubscriptionRedeem(ctx, token) {
            return GetGiftSubscriptionRedeemPreview(token).then((response) => {
                return response?.data || null;
            });
        },
        redeemGiftSubscription(ctx, token) {
            return RedeemGiftSubscription(token).then((response) => {
                if (response?.data?.subscription) {
                    ctx.dispatch('updateCurrentSubscription', response.data.subscription);
                }
                return response?.data || null;
            });
        },
        generateSticker(ctx, prompt) {
          ctx.commit('setGenerationInProgress', true);
          let attempt = ctx.getters.currentAttempt;
          const req = {
            session_uuid: ctx.state.effectGenerationSession.uuid,
            attempt_uuid: attempt.uuid,
            prompt: prompt,
            style_preset_id: ctx.state.selectedEffectGenerationStylePresetID
          };
          attempt.status = EffectGenerationAttemptStatus.Processing;
          attempt.prompt = prompt;
          attempt.stylePresetID = ctx.state.selectedEffectGenerationStylePresetID;
          ctx.commit('updateAttempt', attempt);
          let future = GenerateEffect(req);
          future.then(
            (response) => {
              if (response.data?.result) {
                attempt.generatedImageURL = response.data.result.generated_image_url;
                attempt.status = response.data.result.status;
              } else {
                attempt.status = EffectGenerationAttemptStatus.Created;
                logError('No data in response while generating effect');
                ctx.dispatch('setErrorMessage', i18n.t('generation.error_while_creating_sticker'));
              }
              ctx.commit('updateAttempt', attempt);
              const stillProcessing = attempt.status === EffectGenerationAttemptStatus.Processing;
              if (stillProcessing) {
                ctx.dispatch('startGenerationStatusPolling');
              } else {
                ctx.dispatch('stopGenerationStatusPolling');
              }
              ctx.commit('setGenerationInProgress', stillProcessing);
            },
            (error) => {
              ctx.dispatch('setErrorMessage', error.response?.data?.error || i18n.t('generation.error_while_creating_sticker'));
              switch (error.response?.status) {
                case 410:
                  ctx.commit('setEffectGenerationSession', null);
                  ctx.commit('setGenerationInProgress', false);
                  return;
                case 409:
                  attempt.uuid = getUUID();
                  attempt.status = EffectGenerationAttemptStatus.Created;
                  break;
                case 429:
                  ctx.dispatch('checkEffectGenerationSession', ctx.state.effectGenerationSession.uuid);
                  ctx.commit('setGenerationInProgress', false);
                  return;
                case 503 || 500:
                    logError('Error while generating effect', error);
                    if (error.response?.data?.attempt_expended) {
                      attempt.status = EffectGenerationAttemptStatus.EffectGenerationError;
                    } else {
                      attempt.status = EffectGenerationAttemptStatus.Created;
                    }
                  break;
                case 400 || 413:
                  if (error.response?.data?.attempt_expended) {
                    attempt.status = error.response?.data?.result?.status;
                  } else {
                    attempt.status = EffectGenerationAttemptStatus.Created;
                    logError('Error while generating effect', error);
                  }
                  break;
                default:
                  if (error.response?.data?.attempt_expended) {
                    attempt.status = EffectGenerationAttemptStatus.UnrecognizedError;
                  } else {
                    attempt.status = EffectGenerationAttemptStatus.Created;
                  }
                  logError('Error while generating effect', error);
              }
              ctx.commit('setGenerationInProgress', false);
              ctx.commit('updateAttempt', attempt);
            }
          );
        },
        sendGeneratedEffect(ctx, data) {
          const future = PostGeneratedEffect(data.sendDonation? "" : ctx.state.effectGenerationSession.attempts[ctx.state.selectedAttemptIndex].uuid,  ctx.state.effectGenerationSession.uuid, data.name, data.text);
          future.then(
            (response) => {
              if (response.data?.success) {
                const target = data.sendDonation
                  ? resolveDesignPath(ctx, '/sent-donation', '/wall/sent-donation')
                  : resolveDesignPath(ctx, '/sent', '/wall/sent');
                router.push(target);
                ctx.dispatch('stopGenerationStatusPolling');
                ctx.commit('setEffectGenerationSession', null);
              } else {
                logError('No data in response while posting generated effect');
                ctx.dispatch('setErrorMessage', i18n.t('generation.error_while_posting_sticker'));
              }
            },
            (error) => {
              logError('Error while posting generated effect', error);
              ctx.dispatch('setErrorMessage', error.response?.data?.error || i18n.t('generation.error_while_posting_sticker'));
            }
          );
        },
        setSecondsUntilNextFreeEffect({ state, commit }, value) {
          if (state.secondsUntilNextFreeEffectIntervalID) {
            clearInterval(state.secondsUntilNextFreeEffectIntervalID);
            commit('setSecondsUntilNextFreeEffectIntervalID', null);
          }
          commit('setSecondsUntilNextFreeEffect', value);
          if (value > 0) {
            const intervalID = setInterval(() => {
              if (state.secondsUntilNextFreeEffect <= 0) {
                clearInterval(state.secondsUntilNextFreeEffectIntervalID);
                commit('setSecondsUntilNextFreeEffectIntervalID', null);
              } else {
                commit('setSecondsUntilNextFreeEffect', state.secondsUntilNextFreeEffect - 1);
              }
            }, 1000);
            commit('setSecondsUntilNextFreeEffectIntervalID', intervalID);
          }
        },
        async reloadWallPosts(ctx) {
          if (!ctx.state.wallEnabled) return;
          try {
            const resp = await GetWallPosts(null);
            const posts = resp.data?.posts || [];
            ctx.commit('setPosts', posts);
            const next = resp.data?.next_cursor || null;
            ctx.commit('setPostsPagination', { nextPageToken: next || null, loading: false });
          } catch (e) {
            logWarn('Error reloading wall posts', e);
          }
        }
    },
});
